import type { IPondParams } from "pages/Ponds/types";

export type EventBusEventSource = "s3" | "storage_gateway";

/** Default selection when opening the form or when API returns no sources */
export const DEFAULT_EVENT_SOURCES: EventBusEventSource[] = ["s3"];

/** API may return `event_bridge_sources` (GET) or `event_sources` (older / POST body). */
export function getEventBridgeSourcesFromParams(
  params: Pick<IPondParams, "event_sources" | "event_bridge_sources">,
): EventBusEventSource[] | undefined {
  const raw = params.event_bridge_sources ?? params.event_sources;
  if (raw == null || !Array.isArray(raw)) return undefined;
  return raw.filter(
    (x): x is EventBusEventSource => x === "s3" || x === "storage_gateway",
  );
}

export const EVENT_SOURCE_LABELS: Record<EventBusEventSource, string> = {
  s3: "S3 bucket events",
  storage_gateway: "Storage Gateway events",
};

/** Build checkbox state from API: empty/unknown → S3 only by default */
export function checkboxStateFromApi(
  sources: EventBusEventSource[] | null | undefined,
): EventBusEventSource[] {
  const list = (sources ?? []).filter(
    (s): s is EventBusEventSource => s === "s3" || s === "storage_gateway",
  );
  if (!list.length) {
    return [...DEFAULT_EVENT_SOURCES];
  }
  return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
}

/** Payload for API: dedupe, stable order */
export function normalizeEventSourcesForApi(
  selected: EventBusEventSource[],
): EventBusEventSource[] {
  return Array.from(new Set(selected)).sort((a, b) => a.localeCompare(b));
}

export function formatEventSourcesForDisplay(
  sources: EventBusEventSource[] | null | undefined,
): string {
  const list = sources?.length ? sources : [];
  if (!list.length) {
    return "Not specified (defaults may apply)";
  }
  return list.map(src => EVENT_SOURCE_LABELS[src]).join(" · ");
}
